import { Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CampaignService } from '../../core/services/api/campaign.service';
import { Campaign } from '../../core/models/campaign/campaign';
import { CampaignStatus } from '../../core/enum/campaign-status.enum';
import { CampaignSummary } from '../../core/models/campaign/campaign-summary';
import { BlockUiService } from '../../shared/services/blockUi/block-ui.service';
import { ErrorService } from '../../core/services/error.service';
import { finalize } from 'rxjs/operators';
import { ToastService } from '../../core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { PopupService } from '../../shared/services/popup/popup.service';
import { PopupOptions } from '../../shared/services/popup/popup-options';

@Component({
    selector: 'app-investor-management',
    templateUrl: './investor-management.component.html',
    styleUrls: ['./investor-management.component.scss'],
    standalone: false
})
export class InvestorManagementComponent implements OnInit {
  @Input() searchText: string = '';
  campaign: Campaign;
  campaignStatus = CampaignStatus;
  campaignSummary: CampaignSummary;
  finalSubmitted = false;
  isPageLoading = true;
  loadError = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private campaignService: CampaignService,
    private blockUiService: BlockUiService,
    private errorService: ErrorService,
    private toastService: ToastService,
    private translate: TranslateService,
    private popupService: PopupService,
  ) {}

  ngOnInit(): void {
    const slug = this.activatedRoute.snapshot.params.slug;

    if (!slug) {
      this.loadError = true;
      this.isPageLoading = false;
      return;
    }

    this.campaignService.getCampaign(slug).subscribe({
      next: (campaign) => {
        if (!campaign) {
          this.loadError = true;
          this.isPageLoading = false;
          return;
        }

        this.campaign = campaign;
        this.getcampaignSummary();
      },
      error: (error) => {
        this.loadError = true;
        this.isPageLoading = false;
        this.errorService.showError(error);
      },
    });
  }

  getcampaignSummary(): void {
    if (!this.campaign?.id) {
      this.loadError = true;
      this.isPageLoading = false;
      return;
    }

    this.campaignService
      .getCampaignSummary(this.campaign.id)
      .pipe(
        finalize(() => {
          this.isPageLoading = false;
        }),
      )
      .subscribe({
        next: (data) => {
          if (!data) {
            this.loadError = true;
            return;
          }

          this.campaignSummary = data;
          this.finalSubmitted = !!data.isFinalized;
          this.loadError = false;
        },
        error: (error) => {
          this.loadError = true;
          this.errorService.showError(error);
        },
      });
  }

  get approvedInvestorProgressPercent(): number {
    const total = this.campaignSummary?.paidInvestors ?? 0;
    const approved = this.campaignSummary?.approvedInvestors ?? 0;
    if (!total || total <= 0) return 0;

    const percent = Math.floor((approved / total) * 100);
    return Math.max(0, Math.min(100, percent));
  }

  get isDueDateOverDue(): boolean {
    const due = this.campaignSummary?.duedate;
    if (!due) return false;

    const dueDate = new Date(due as any);
    if (isNaN(dueDate.getTime())) return false;

    const dueDateEndOfDay = new Date(
      dueDate.getFullYear(),
      dueDate.getMonth(),
      dueDate.getDate(),
      23,
      59,
      59,
      999,
    );

    return dueDateEndOfDay.getTime() < Date.now();
  }

  IsShowSubTitle(): boolean {
    const status = this.campaign?.campaignStatus;

    if (status == null) {
      return false;
    }

    return (
      status === CampaignStatus.PreLive ||
      status === CampaignStatus.Live ||
      status === CampaignStatus.Cooling ||
      (status === CampaignStatus.ShareAllotment &&
        this.campaignSummary?.successFee == null)
    );
  }

  submitFinal(): void {
    if (this.finalSubmitted) {
      this.toastService.success(this.translate.instant('toast.campaignFinalized'));
      return;
    }

    const popupOptions: PopupOptions = {
      titleLabel: 'Confirm Submission',
      okLabel: 'Yes',
      cancelLabel: 'No',
    };

    this.popupService
      .confirm(
        'By submitting this list, you are confirming it is final and complete. Once submitted, the investor list will be locked and you will no longer be able to make any changes. Are you sure you want to proceed?',
        popupOptions,
      )
      .then(() => {
        this.blockUiService.open();
        this.campaignService
          .finalizeCampaign(this.campaign.id)
          .pipe(
            finalize(() => {
              this.blockUiService.close();
            }),
          )
          .subscribe({
            next: (res) => {
              this.finalSubmitted = !!res?.isFinalized;
              if (this.campaignSummary) this.campaignSummary.isFinalized = this.finalSubmitted;
              if (this.finalSubmitted) this.toastService.success(this.translate.instant('toast.campaignFinalized'));
              else this.toastService.error(this.translate.instant('toast.unableToFinalizeCampaign'));
            },
            error: (err) => {
              this.errorService.showError(err);
            },
          });
      })
      .catch(() => {});
  }
}
